import {
  defaultValue,
  dynamicValue,
  Events,
  getEllipsesStyles,
  getValueByPath,
  humanizeCamelCase,
  IWork,
  Loader,
  useEnvironment,
  useParams,
  useWorkPageStore,
  UserAvatar,
  useEntityPageStore,
} from '@diana/core';
import * as Icons from '@mui/icons-material';
import { Box, Stack, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { ReactNode, useCallback } from 'react';

interface CommonProps {
  labelKey: string;
  dateKey: string | null;
  dataPath: string | null;
  dataLabel: string | null;
  untitledLabel: string;
  statusIcon?: string;
  statusIconColor?:
    | 'warning'
    | 'inherit'
    | 'disabled'
    | 'action'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | undefined;
  statusIconShowsWhen?: string;
  statusIconTooltipText?: string;
}

function Title({ children, sx }: { children: ReactNode; sx?: SxProps }) {
  return (
    // @ts-ignore
    <Typography sx={{ fontWeight: 500, ...getEllipsesStyles(), ...(sx || {}) }}>
      {children}
    </Typography>
  );
}

const getterByStore = (store: any, type: string) => store((state: any) => state[type]?.id);

export function NavigationWork({
  work,
  labelKey,
  dateKey,
  dataPath,
  dataLabel,
  isEntities,
  untitledLabel,
  onClick,
  statusIcon = 'WarningAmberRounded',
  statusIconShowsWhen = '',
  statusIconColor = 'warning',
  statusIconTooltipText = '',
  peoplePage,
}: CommonProps & {
  work: any;
  onClick?: (id: string) => void;
  isEntities?: boolean;
  peoplePage?: boolean;
}) {
  const environment = useEnvironment();
  const { workId: workIdFromUrl } = useParams<{ workId: string }>();
  const { userId: userFromUrl } = useParams<{ userId: string }>();
  const fromUrl = peoplePage ? userFromUrl : workIdFromUrl;
  const { palette, shape } = useTheme();
  const workIdFromStore = isEntities
    ? getterByStore(useEntityPageStore, 'entity')
    : getterByStore(useWorkPageStore, 'work');

  const isSelected = workIdFromStore === work.id;

  const workDate = work[dateKey ?? 'lastModified'];
  const isLoading = fromUrl !== workIdFromStore && work.id === fromUrl;
  const secondaryValue: string | null =
    dataPath !== null
      ? getValueByPath({ json: work.additionalProperties, path: dataPath })
      : workDate?.date ?? workDate;
  const secondaryLabel: string =
    dataLabel !== undefined && dataLabel !== null
      ? dataLabel
      : humanizeCamelCase(dateKey ?? 'lastModified');

  const onWorkClicked = useCallback(() => {
    onClick?.(work.id);
    peoplePage
      ? Events.OpenUser.post({ userId: work.id, environment, openInNewTab: false })
      : isEntities
      ? Events.OpenEntity.post({ thingId: work.id, environment, openInNewTab: false })
      : Events.OpenWork.post({ workId: work.id, environment, openInNewTab: false });
  }, [environment, isEntities, onClick, work.id, peoplePage]);

  const Icon = Icons[statusIcon as keyof typeof Icons] ?? Icons.WarningAmberRounded;
  const iconCondition = statusIconShowsWhen
    ? dynamicValue(statusIconShowsWhen, { data: work.data })
    : false;

  const showIcon = iconCondition === statusIconShowsWhen ? false : iconCondition;

  return (
    <Stack
      id={`sidenav-${work.id}`}
      className="navigation-work-item"
      direction="row"
      alignItems="center"
      onClick={onWorkClicked}
      sx={{
        px: 2,
        py: 0.5,
        cursor: 'pointer',
        borderRadius: `${shape.borderRadius}px`,
        backgroundColor: isSelected ? palette.action.hover : 'transparent',
        '&:hover': { backgroundColor: palette.action.hover },
        justifyContent: peoplePage ? 'flex-start' : 'space-between',
      }}
    >
      {peoplePage && (
        <UserAvatar
          user={{ name: work[labelKey] }}
          tooltip={false}
          sx={{ fontSize: '12px', width: '32px', height: '32px', mr: 1 }}
        />
      )}
      <Box sx={{ maxWidth: 'calc(100% - 46px)' }}>
        <Title>{work[labelKey] ?? untitledLabel}</Title>
        <Typography variant="subtitle2">
          {secondaryLabel} {secondaryValue ?? defaultValue}
        </Typography>
      </Box>
      <Stack direction="row" justifyContent="space-around" alignItems="center">
        <Loader
          simple
          progressProps={{ size: 14, sx: { mr: !!showIcon ? 1 : 0 } }}
          show={isLoading}
        />
        {!!showIcon ? (
          <Tooltip title={statusIconTooltipText} placement="top">
            <Icon fontSize="small" color={statusIconColor} />
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function NavigationGroup({
  group,
  works,
  labelKey,
  dateKey,
  untitledLabel,
  onItemClick,
  isEntities,
  peoplePage,
  ...rest
}: CommonProps & {
  group: string;
  works: IWork[];
  onItemClick?: (id: string) => void;
  isEntities?: boolean;
  peoplePage?: boolean;
}) {
  return (
    <Box sx={{ maxWidth: '100%' }}>
      {group ? <Title sx={{ mb: 1.5 }}>{group}</Title> : null}
      <Box sx={{ '& .navigation-work-item:last-of-type': { mb: 1.5 } }}>
        {works.map((work) => (
          <NavigationWork
            isEntities={isEntities}
            key={work.id}
            work={work}
            labelKey={labelKey}
            dateKey={dateKey}
            untitledLabel={untitledLabel}
            onClick={onItemClick}
            peoplePage={peoplePage}
            {...rest}
          />
        ))}
      </Box>
    </Box>
  );
}
